import { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Alert,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useIntegrations,
  useSaveIntegration,
  useClearIntegration,
} from './useIntegrations';
import { IntegrationEditModal } from './IntegrationEditModal';

/**
 * Integrations — Per-Tenant Provider-Credentials.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/admin-integrations-and-settings-shapes.md
 *
 * Server liefert pro Provider:
 *  - `configSchema` → Edit-Modal rendert schema-getriebene Form
 *  - `source` ('tenant' / 'env' / null) → Status-Badge
 *  - `preview` → maskierte/klartextliche Feld-Vorschau
 *  - `notes` → freier Operator-Text
 *  - `centralOnly` → PUT blockiert (z.B. lexoffice via env-Flag)
 */
export function IntegrationsPage() {
  const query = useIntegrations();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="Integrations" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="Integrations" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const items = query.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Per-Tenant Provider-Credentials · GET /v1/admin/integrations"
      />
      <Alert color="info" className="small">
        Secret-Werte werden nie an das Frontend zurückgesendet — Eingaben in
        Password-Feldern beim Bearbeiten <strong>überschreiben</strong> bestehende
        Werte. Leere Eingabe in einem Secret-Feld = Wert bleibt erhalten.
        Komplett-Löschen entfernt die Provider-Row für diesen Tenant.
      </Alert>

      <Row className="g-3">
        {items.map((item) => (
          <Col md="6" lg="4" key={item.name}>
            <ProviderCard item={item} canWrite={canWrite} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

function ProviderCard({ item, canWrite }) {
  const [editOpen, setEditOpen] = useState(false);
  const [pendingClear, setPendingClear] = useState(false);
  const save = useSaveIntegration();
  const clear = useClearIntegration();

  const sourceBadge = sourceMeta(item.source);
  const busy = save.isPending || clear.isPending;

  const onSave = async (config, notes) => {
    await save.mutateAsync({ provider: item.name, config, notes });
    setEditOpen(false);
  };

  const onClear = async () => {
    await clear.mutateAsync({ provider: item.name });
    setPendingClear(false);
  };

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="flex-grow-1">
            <CardTitle tag="h5" className="mb-1">{item.displayName}</CardTitle>
            <p className="small text-muted mb-0">{item.description}</p>
          </div>
          <Badge color={sourceBadge.color} pill>{sourceBadge.label}</Badge>
        </div>

        {item.centralOnly && (
          <Alert color="warning" className="small mt-2 mb-2 py-2">
            Provider ist <code>centralOnly</code> — Tenant-spezifische Configs
            sind durch die Server-Env gesperrt. Bearbeiten ist deaktiviert,
            DELETE bleibt erlaubt zum Aufräumen alter Rows.
          </Alert>
        )}

        <PreviewBlock item={item} />

        {item.notes && (
          <div className="small mt-2">
            <span className="text-muted">Notes:</span>{' '}
            <span className="fst-italic">{item.notes}</span>
          </div>
        )}

        <HasPermission perms={['integrations.write']}>
          <div className="d-flex gap-2 mt-3">
            <Button
              color="primary"
              size="sm"
              outline
              onClick={() => setEditOpen(true)}
              disabled={busy || (item.centralOnly && item.source !== 'tenant')}
            >
              {item.source === 'tenant' ? 'Bearbeiten' : 'Konfigurieren'}
            </Button>
            {item.source === 'tenant' && (
              <Button
                color="danger"
                size="sm"
                outline
                onClick={() => setPendingClear(true)}
                disabled={busy}
              >
                Entfernen
              </Button>
            )}
          </div>
        </HasPermission>
      </CardBody>

      {editOpen && (
        <IntegrationEditModal
          item={item}
          onSubmit={onSave}
          onClose={() => setEditOpen(false)}
          busy={save.isPending}
          canWrite={canWrite}
        />
      )}

      <ConfirmDialog
        isOpen={pendingClear}
        title={`${item.displayName}: Credentials entfernen?`}
        body={
          <>
            Alle Per-Tenant-Credentials für <strong>{item.displayName}</strong>{' '}
            werden gelöscht.{' '}
            {item.hasEnvFallback
              ? 'Nach dem Löschen läuft der Provider mit dem env-Default.'
              : 'Ohne env-Fallback fällt jede Funktion aus, die diesen Provider braucht.'}
          </>
        }
        confirmLabel="Entfernen"
        confirmColor="danger"
        busy={clear.isPending}
        onConfirm={onClear}
        onCancel={() => setPendingClear(false)}
      />
    </Card>
  );
}

function sourceMeta(source) {
  if (source === 'tenant') return { color: 'success', label: '✓ konfiguriert' };
  if (source === 'env') return { color: 'info', label: 'Env-Default aktiv' };
  return { color: 'secondary', label: 'nicht konfiguriert' };
}

function PreviewBlock({ item }) {
  if (!item.preview) {
    return (
      <div className="small text-muted mt-2">
        {item.source === 'env'
          ? 'Läuft mit Server-Env-Defaults — keine Tenant-Overrides aktiv.'
          : 'Noch keine Credentials gesetzt.'}
      </div>
    );
  }
  return (
    <dl className="small mb-0 mt-2 font-monospace">
      {item.configSchema.map((field) => {
        const value = item.preview[field.key];
        if (value === undefined) return null;
        return (
          <div key={field.key} className="d-flex gap-2 mb-1">
            <dt style={{ minWidth: 110 }} className="text-muted text-truncate">
              {field.key}
            </dt>
            <dd className="mb-0 text-truncate flex-grow-1" title={String(value)}>
              {String(value)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
